'use client';

import Script from 'next/script';
import { useEffect, useMemo, useState } from 'react';
import {
	canSendMarketingFromState,
	getAnalyticsPrivacyState,
	subscribeAnalyticsPrivacyState,
} from '@/lib/analytics/privacy-state';

interface ParsedScript {
	src?: string | null;
	async?: boolean;
	defer?: boolean;
	type?: string;
	crossOrigin?: 'anonymous' | 'use-credentials';
	referrerPolicy?: ScriptReferrerPolicy;
	content?: string;
}

type ScriptReferrerPolicy =
	| ''
	| 'no-referrer'
	| 'no-referrer-when-downgrade'
	| 'origin'
	| 'origin-when-cross-origin'
	| 'same-origin'
	| 'strict-origin'
	| 'strict-origin-when-cross-origin'
	| 'unsafe-url';

export interface CustomPixel {
	name: string;
	enabled: boolean;
	placement: 'head' | 'bodyStart' | 'bodyEnd';
	code: string;
	id?: string;
}

const LOAD_MARKETING_PIXELS_ON_PAGE_LOAD =
	process.env.NEXT_PUBLIC_LOAD_MARKETING_PIXELS_ON_PAGE_LOAD === 'true';
const USER_INTENT_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
// Upper bound on how long one staggered pixel waits for an idle period. Kept
// short so even a busy main thread finishes the chain within a few seconds —
// well inside the analytics client's 10s vendor-event retry window.
const PIXEL_REVEAL_IDLE_TIMEOUT_MS = 500;

// One shared queue across every CustomPixels instance (head / bodyStart /
// bodyEnd), so pixels from different placements are also spaced apart: each
// task runs in its own idle callback.
const pixelRevealQueue: Array<() => void> = [];
let pixelRevealScheduled = false;

function schedulePixelReveal(task: () => void) {
	pixelRevealQueue.push(task);
	pumpPixelRevealQueue();
}

function pumpPixelRevealQueue() {
	if (pixelRevealScheduled || pixelRevealQueue.length === 0) return;
	pixelRevealScheduled = true;

	const run = () => {
		pixelRevealScheduled = false;
		pixelRevealQueue.shift()?.();
		pumpPixelRevealQueue();
	};

	if (typeof requestIdleCallback === 'function') {
		requestIdleCallback(run, { timeout: PIXEL_REVEAL_IDLE_TIMEOUT_MS });
	} else {
		setTimeout(run, 0);
	}
}

/**
 * Renders custom tracking pixels / scripts injected via the CMS.
 *
 * - `head` placement  -> strategy "afterInteractive" (loads async, never render-blocking)
 * - heavy marketing pixels are pushed to "lazyOnload" to keep them out of the critical path
 * - `bodyStart`       -> strategy "afterInteractive" (runs after hydration)
 * - `bodyEnd`         -> strategy "lazyOnload" (runs when browser is idle)
 *
 * Analytics / remarketing / tag-manager pixels are force-deferred to
 * "lazyOnload" regardless of placement (see DEFERRED_VENDOR_HOSTS /
 * DEFERRED_VENDOR_PATTERNS): Klaviyo, vibe.co, the Snapchat Pixel, the TikTok
 * Pixel, the Meta/Facebook pixel base, Google Tag Manager / gtag.js / dataLayer
 * snippets, and Google Ads / Doubleclick conversion tags. Their page_view /
 * standard events fire reliably at idle, so this cuts TBT/TTI without losing
 * events. Postscript is the one vendor kept on its placement strategy
 * ("afterInteractive" for head/bodyStart) — see the list comment below.
 *
 * On top of the strategy, every pixel except the eager attribution ones (Triple
 * Whale) waits for consent AND the first user interaction, then mounts one at a
 * time — one pixel per idle callback, in CMS order — so the first tap/scroll
 * doesn't inject and run every vendor bootstrap in the same task.
 *
 * `beforeInteractive` is intentionally never used: it blocks first render, and
 * because this component mounts in a nested layout (not the root layout) Next
 * would warn and silently degrade it anyway. External `src` scripts are also
 * clamped so a CMS-authored pixel can never reintroduce a render-blocking tag.
 */
export function CustomPixels({
	pixels,
	placement,
	managedMetaPixelId,
}: {
	pixels: CustomPixel[];
	placement: 'head' | 'bodyStart' | 'bodyEnd';
	managedMetaPixelId?: string | null;
}) {
	const filtered = useMemo(
		() =>
			pixels.filter(
				(p) =>
					p.enabled &&
					p.placement === placement &&
					p.code &&
					!isManagedMetaPixelSnippet(p.code, managedMetaPixelId),
			),
		[pixels, placement, managedMetaPixelId],
	);
	const blockedManagedMetaPixel = useMemo(
		() =>
			pixels.find(
				(p) =>
					p.enabled &&
					p.placement === placement &&
					p.code &&
					isManagedMetaPixelSnippet(p.code, managedMetaPixelId),
			),
		[pixels, placement, managedMetaPixelId],
	);
	const [privacyState, setPrivacyState] = useState(getAnalyticsPrivacyState);
	const [hasUserIntent, setHasUserIntent] = useState(LOAD_MARKETING_PIXELS_ON_PAGE_LOAD);
	// How many intent-gated pixels have been revealed by the staggered queue.
	const [revealedCount, setRevealedCount] = useState(0);
	const marketingAllowed = canSendMarketingFromState(privacyState);
	const eagerPixels = filtered.filter(isEagerAttributionPixel);
	const gatedPixels = filtered.filter((pixel) => !isEagerAttributionPixel(pixel));
	const gatedPixelCount = gatedPixels.length;

	useEffect(() => subscribeAnalyticsPrivacyState(setPrivacyState), []);

	useEffect(() => {
		if (hasUserIntent) return;

		const markUserIntent = () => setHasUserIntent(true);
		for (const eventName of USER_INTENT_EVENTS) {
			window.addEventListener(eventName, markUserIntent, { once: true, passive: true });
		}

		return () => {
			for (const eventName of USER_INTENT_EVENTS) {
				window.removeEventListener(eventName, markUserIntent);
			}
		};
	}, [hasUserIntent]);

	// Once consent + intent allow the gated pixels, reveal them one per idle
	// callback instead of all in the interaction's task.
	useEffect(() => {
		if (!hasUserIntent || !marketingAllowed || gatedPixelCount === 0) return;

		let cancelled = false;
		for (let index = 0; index < gatedPixelCount; index += 1) {
			schedulePixelReveal(() => {
				if (!cancelled) setRevealedCount((count) => Math.max(count, index + 1));
			});
		}

		return () => {
			cancelled = true;
		};
	}, [hasUserIntent, marketingAllowed, gatedPixelCount]);

	useEffect(() => {
		if (process.env.NODE_ENV === 'production') return;

		if (blockedManagedMetaPixel) {
			console.warn(
				`[CustomPixels] Meta Pixel snippet "${blockedManagedMetaPixel.name}" was blocked because the managed pixel ${managedMetaPixelId} is active. ` +
					'The managed pixel + Conversions API already handle Meta tracking with matching event IDs.',
			);
			return;
		}

		const strayMetaPixel = filtered.find((pixel) => looksLikeMetaPixelBaseCode(pixel.code));
		if (!strayMetaPixel) return;

		console.warn(
			`[CustomPixels] Meta Pixel snippet "${strayMetaPixel.name}" is active but analytics.meta.pixelId is not configured. ` +
				'The managed pixel and Conversions API are disabled, so these events fire un-deduplicated. ' +
				'Set analytics.meta.pixelId in SiteSettings.',
		);
	}, [blockedManagedMetaPixel, filtered, managedMetaPixelId]);

	// Attribution-critical pixels skip the user-intent gate (but never the consent
	// gate): Triple Whale's session capture is the join key for paid-order
	// attribution, so deferring it to first interaction risks losing the landing
	// visit — bounces, instant checkouts, and automated QA captures all show a
	// dead pixel.
	// Gated pixels only count up after intent + consent (effect above).
	const renderable = [...eagerPixels, ...gatedPixels.slice(0, revealedCount)];

	if (renderable.length === 0 || !marketingAllowed) {
		return null;
	}

	return (
		<>
			{renderable.flatMap((pixel) => {
				const pixelKey = pixel.id || pixel.name.toLowerCase().replace(/\s+/g, '-');
				const scripts = extractScripts(pixel.code);
				const postscriptBootstrap = buildPostscriptBootstrap(scripts);
				const strategy = resolvePixelStrategy(pixel.placement, scripts);

				if (postscriptBootstrap) {
					return (
						<Script
							key={`pixel-${pixelKey}-postscript`}
							id={`pixel-${pixelKey}-postscript`}
							strategy={strategy}
							// biome-ignore lint/security/noDangerouslySetInnerHtml: intentional — injecting CMS-managed tracking scripts
							dangerouslySetInnerHTML={{ __html: postscriptBootstrap }}
						/>
					);
				}

				return scripts.map((script, index) => {
					const scriptId = `pixel-${pixelKey}-${index + 1}`;

					if (script.src) {
						return (
							<Script
								key={scriptId}
								id={scriptId}
								src={script.src}
								strategy={resolveScriptStrategy(strategy, script)}
								async={script.async}
								defer={script.defer}
								type={script.type}
								crossOrigin={script.crossOrigin}
								referrerPolicy={script.referrerPolicy}
							/>
						);
					}

					return (
						<Script
							key={scriptId}
							id={scriptId}
							strategy={resolveScriptStrategy(strategy, script)}
							type={script.type}
							// biome-ignore lint/security/noDangerouslySetInnerHtml: intentional — injecting CMS-managed tracking scripts
							dangerouslySetInnerHTML={{ __html: script.content ?? '' }}
						/>
					);
				});
			})}
		</>
	);
}

// SAFE TO DEFER (analytics / remarketing / tag-manager): page_view + standard
// events fire reliably once the browser is idle, so loading these at
// 'lazyOnload' trims TBT/TTI and frees early bandwidth for the LCP hero image
// without meaningfully dropping events.
//   - Klaviyo            (s.static.klaviyo.com, static.klaviyo.com)
//   - vibe.co            (s.vibe.co)
//   - Snapchat Pixel     (sc-static.net, tr.snapchat.com)
//   - TikTok Pixel       (analytics.tiktok.com)
//   - Meta/Facebook base (connect.facebook.net + fbevents content match below)
//   - Google Tag Manager / gtag.js (googletagmanager.com) and Google Ads /
//     Doubleclick (googleads.g.doubleclick.net, googleadservices.com): GTM was
//     the dominant eager, High-priority download competing with the LCP hero
//     image on the throttled link. Marketing accepted the small
//     attribution-timing tradeoff of loading it at idle; conversions still fire,
//     just slightly later.
//
// KEPT EAGER (intentionally NOT in these lists):
//   - Postscript: its popup frequency-capping depends on the SDK loading in its
//     configured placement, so we do not defer it to lazyOnload.
const DEFERRED_VENDOR_HOSTS = new Set([
	's.static.klaviyo.com',
	'static.klaviyo.com',
	's.vibe.co',
	'sc-static.net',
	'tr.snapchat.com',
	'analytics.tiktok.com',
	'connect.facebook.net',
	'www.googletagmanager.com',
	'googleads.g.doubleclick.net',
	'www.googleadservices.com',
]);

// Pixels whose whole value is session/attribution capture on the landing view.
// They render as soon as consent allows, without waiting for user intent.
const EAGER_ATTRIBUTION_PATTERNS = [/TriplePixel/i];

function isEagerAttributionPixel(pixel: CustomPixel) {
	return EAGER_ATTRIBUTION_PATTERNS.some((pattern) => pattern.test(pixel.code));
}

const DEFERRED_VENDOR_PATTERNS = [
	/klaviyo/i,
	/vibe/i,
	/snapchat/i,
	/tiktok/i,
	/fbevents/i,
	/googletagmanager/i,
	/gtag\(/i,
	/dataLayer/i,
];

function resolvePixelStrategy(
	placement: 'head' | 'bodyStart' | 'bodyEnd',
	scripts: ParsedScript[],
): 'beforeInteractive' | 'afterInteractive' | 'lazyOnload' {
	const strategyMap: Record<string, 'beforeInteractive' | 'afterInteractive' | 'lazyOnload'> = {
		head: 'afterInteractive',
		bodyStart: 'afterInteractive',
		bodyEnd: 'lazyOnload',
	};

	return scripts.some(isDeferrableVendorScript) ? 'lazyOnload' : strategyMap[placement];
}

function resolveScriptStrategy(
	defaultStrategy: 'beforeInteractive' | 'afterInteractive' | 'lazyOnload',
	script: ParsedScript,
) {
	if (isDeferrableVendorScript(script)) return 'lazyOnload';

	// Never let an external script block first render. `beforeInteractive` is
	// reserved for inline bootstraps in the root layout; an external `src` tag
	// here should load async (afterInteractive) at the earliest.
	if (script.src && defaultStrategy === 'beforeInteractive') return 'afterInteractive';

	return defaultStrategy;
}

function isDeferrableVendorScript(script: ParsedScript) {
	const url = parseScriptUrl(script.src);
	if (url && DEFERRED_VENDOR_HOSTS.has(url.hostname)) {
		return true;
	}

	const content = script.content;

	return Boolean(content && DEFERRED_VENDOR_PATTERNS.some((pattern) => pattern.test(content)));
}

export function isManagedMetaPixelSnippet(
	code: string,
	managedMetaPixelId: string | null | undefined,
) {
	return Boolean(managedMetaPixelId?.trim() && looksLikeMetaPixelBaseCode(code));
}

/**
 * Loose detection of Meta Pixel base code (init/track calls or the fbevents.js
 * loader). The managed pixel + Conversions API own Meta tracking, and CMS
 * snippets fire un-deduplicated events without the storefront eventID.
 */
function looksLikeMetaPixelBaseCode(code: string) {
	return (
		/fbevents\.js/i.test(code) ||
		/fbq\s*\(\s*['"](?:init|track|trackCustom|trackSingle|trackSingleCustom)['"]/i.test(code)
	);
}

/**
 * Extracts individual script blocks so external sources and inline
 * initialization code preserve their original order.
 */
function extractScripts(html: string) {
	const scriptTagRegex = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
	const scripts: ParsedScript[] = [...html.matchAll(scriptTagRegex)].map((match) => {
		const attributes = match[1] || '';
		const content = match[2]?.trim() || undefined;

		return {
			src: readScriptAttribute(attributes, 'src'),
			async: hasBooleanAttribute(attributes, 'async'),
			defer: hasBooleanAttribute(attributes, 'defer'),
			type: readScriptAttribute(attributes, 'type') ?? undefined,
			crossOrigin:
				(readScriptAttribute(attributes, 'crossorigin') as
					| 'anonymous'
					| 'use-credentials'
					| null) ?? undefined,
			referrerPolicy:
				(readScriptAttribute(attributes, 'referrerpolicy') as ScriptReferrerPolicy | null) ??
				undefined,
			content,
		};
	});

	if (scripts.length > 0) {
		const hasPostscriptLoader = scripts.some((script) => isPostscriptLoaderScript(script.src));

		if (hasPostscriptLoader) {
			// Postscript's loader injects sdk.bundle.js itself, so keeping both scripts
			// causes the SDK to initialize twice.
			return scripts.filter((script) => !isPostscriptBundleScript(script.src));
		}

		return scripts;
	}

	const trimmed = html.trim();
	return trimmed ? [{ content: trimmed }] : [];
}

function isPostscriptLoaderScript(src: string | null | undefined) {
	const url = parseScriptUrl(src);
	return (
		url?.hostname === 'sdk.postscript.io' && url.pathname.endsWith('/sdk-script-loader.bundle.js')
	);
}

function isPostscriptBundleScript(src: string | null | undefined) {
	const url = parseScriptUrl(src);
	return url?.hostname === 'sdk.postscript.io' && url.pathname.endsWith('/sdk.bundle.js');
}

function buildPostscriptBootstrap(scripts: ParsedScript[]) {
	const hasPostscriptLoader = scripts.some((script) => isPostscriptLoaderScript(script.src));

	if (!hasPostscriptLoader) return null;

	const hasUnsupportedExternalScripts = scripts.some(
		(script) =>
			Boolean(script.src) &&
			!isPostscriptLoaderScript(script.src) &&
			!isPostscriptBundleScript(script.src),
	);

	if (hasUnsupportedExternalScripts) return null;

	const bootstrapSteps: string[] = [];
	let loaderInserted = false;

	for (const script of scripts) {
		if (script.content) {
			bootstrapSteps.push(script.content);
			continue;
		}

		if (!script.src || isPostscriptBundleScript(script.src) || loaderInserted) {
			continue;
		}

		if (isPostscriptLoaderScript(script.src)) {
			loaderInserted = true;
			bootstrapSteps.push(buildPostscriptLoaderInsert(script));
		}
	}

	if (bootstrapSteps.length === 0) return null;

	return `(() => {
	${bootstrapSteps.join('\n\n')}
})();`;
}

function buildPostscriptLoaderInsert(script: ParsedScript) {
	if (!script.src) return '';

	const assignments = [
		`loader.src = ${JSON.stringify(script.src)};`,
		`loader.async = ${script.async ? 'true' : 'false'};`,
		`loader.defer = ${script.defer ? 'true' : 'false'};`,
		"loader.dataset.postscriptLoader = 'true';",
	];

	if (script.type) {
		assignments.push(`loader.type = ${JSON.stringify(script.type)};`);
	}

	if (script.crossOrigin) {
		assignments.push(`loader.crossOrigin = ${JSON.stringify(script.crossOrigin)};`);
	}

	if (script.referrerPolicy) {
		assignments.push(`loader.referrerPolicy = ${JSON.stringify(script.referrerPolicy)};`);
	}

	return `if (!document.querySelector('script[data-postscript-loader="true"]')) {
		const loader = document.createElement('script');
		${assignments.join('\n\t\t')}
		document.head.appendChild(loader);
	}`;
}

function parseScriptUrl(src: string | null | undefined) {
	if (!src) return null;

	try {
		return new URL(src, 'https://example.invalid');
	} catch {
		return null;
	}
}

function readScriptAttribute(attributes: string, name: string) {
	const pattern = new RegExp(`${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');
	const match = attributes.match(pattern);
	return match?.[1] || match?.[2] || match?.[3] || null;
}

function hasBooleanAttribute(attributes: string, name: string) {
	return new RegExp(`\\b${name}\\b`, 'i').test(attributes);
}
